import React from 'react';
import SEO from './SEO';
import { getPageSEO } from '../utils/seo';
import './LegalPages.css';

const TermsOfService = () => {
  const pageSEO = getPageSEO('terms-of-service');
  return (
    <>
      <SEO title={pageSEO.title} description={pageSEO.description} keywords={pageSEO.keywords} url={pageSEO.url} type={pageSEO.type} />
    <div className="legal-page-container">
      <div className="legal-content">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last Updated: October 11, 2025</p>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing and using this matrimonial/dating platform ("Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these Terms of Service, please do not use the Service.
          </p>
        </section>

        <section>
          <h2>2. User Eligibility</h2>
          <p>
            <strong>Age Requirement:</strong> You must be at least 18 years of age to use this Service. By registering, you represent and warrant that you are of legal age to form a binding contract.
          </p>
          <p>
            <strong>Legal Capacity:</strong> You must have the legal capacity to enter into a binding agreement and are not prohibited from using the Service under applicable laws.
          </p>
        </section>

        <section>
          <h2>3. Account Responsibilities</h2>
          <ul>
            <li>You are responsible for maintaining the confidentiality of your account credentials</li>
            <li>You agree to provide accurate, current, and complete information during registration</li>
            <li>You are responsible for all activities that occur under your account</li>
            <li>You must notify us immediately of any unauthorized use of your account</li>
            <li>You may not transfer or sell your account to another person</li>
          </ul>
        </section>

        <section>
          <h2>4. Prohibited Conduct</h2>
          <p>You agree NOT to:</p>
          <ul>
            <li>Create fake or misleading profiles</li>
            <li>Harass, abuse, or harm other users</li>
            <li>Use the Service for commercial solicitation or spam</li>
            <li>Upload offensive, inappropriate, or illegal content</li>
            <li>Violate any applicable laws or regulations</li>
            <li>Impersonate any person or entity</li>
            <li>Scrape, copy, or harvest user data without permission</li>
            <li>Use automated systems or bots</li>
          </ul>
        </section>

        <section>
          <h2>5. Content Rights & Ownership</h2>
          <p>
            <strong>User Content:</strong> You retain ownership of content you upload (photos, text, etc.), but you grant us a worldwide, non-exclusive, royalty-free license to use, display, and distribute your content for the purpose of providing the Service.
          </p>
          <p>
            <strong>Content Removal:</strong> We reserve the right to remove any content that violates these terms or is deemed inappropriate.
          </p>
        </section>

        <section>
          <h2>6. Matchmaking Disclaimer</h2>
          <div className="disclaimer-box">
            <p>
              <strong>⚠️ IMPORTANT DISCLAIMER:</strong> We do not guarantee that you will find a match, partner, or spouse through this Service. Success depends on individual compatibility, effort, and circumstances beyond our control.
            </p>
            <p>
              We are a platform that facilitates connections but cannot guarantee outcomes. Matrimonial and relationship success is the responsibility of the individuals involved.
            </p>
          </div>
        </section>

        <section>
          <h2>7. No Background Checks</h2>
          <div className="disclaimer-box">
            <p>
              <strong>⚠️ USER SAFETY NOTICE:</strong> We do not conduct criminal background checks, identity verification, or investigate the backgrounds of our users.
            </p>
            <p>
              You are solely responsible for exercising caution and due diligence when interacting with other users. We strongly recommend:
            </p>
            <ul>
              <li>Meeting in public places</li>
              <li>Informing friends/family of your plans</li>
              <li>Verifying user information independently</li>
              <li>Trusting your instincts</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>8. User Responsibility for Interactions</h2>
          <p>
            <strong>You are solely responsible for your interactions with other users.</strong> We are not responsible for the conduct of any user, whether online or offline. You agree to take reasonable precautions in all interactions.
          </p>
        </section>

        <section>
          <h2>9. Privacy & Data Protection</h2>
          <p>
            Your use of the Service is also governed by our <a href="/privacy">Privacy Policy</a>, which is incorporated by reference into these Terms of Service.
          </p>
        </section>

        <section>
          <h2>10. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR:
          </p>
          <ul>
            <li>Any indirect, incidental, special, or consequential damages</li>
            <li>Loss of profits, data, or goodwill</li>
            <li>Service interruptions or technical issues</li>
            <li>Actions or conduct of other users</li>
            <li>Failure to find a suitable match</li>
          </ul>
          <p>
            Our total liability shall not exceed the amount paid by you, if any, for the Service in the past 12 months.
          </p>
        </section>

        <section>
          <h2>11. Indemnification</h2>
          <p>
            You agree to indemnify and hold us harmless from any claims, damages, losses, or expenses arising from:
          </p>
          <ul>
            <li>Your use of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of another person</li>
            <li>Content you submit to the Service</li>
          </ul>
        </section>

        <section>
          <h2>12. Termination</h2>
          <p>
            <strong>Our Rights:</strong> We reserve the right to suspend or terminate your account at any time, with or without notice, for violations of these Terms or for any other reason.
          </p>
          <p>
            <strong>Your Rights:</strong> You may close your account at any time through your account settings.
          </p>
          <p>
            <strong>Effect of Termination:</strong> Upon termination, your right to use the Service will immediately cease. We may retain certain information as required by law.
          </p>
        </section>

        <section>
          <h2>13. Dispute Resolution</h2>
          <p>
            <strong>Governing Law:</strong> These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to its conflict of law provisions.
          </p>
          <p>
            <strong>Arbitration:</strong> Any disputes arising from these Terms shall be resolved through binding arbitration, except where prohibited by law.
          </p>
        </section>

        <section>
          <h2>14. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will notify users of material changes via email or platform notification. Continued use of the Service after changes constitutes acceptance of the new Terms.
          </p>
        </section>

        <section>
          <h2>15. Contact Information</h2>
          <p>
            If you have questions about these Terms of Service, please contact us at:
          </p>
          <p className="contact-info">
            <strong>Email:</strong> support@l3v3lmatches.com<br />
            <strong>Address:</strong> L3V3L Matches Inc., San Francisco, CA, USA<br />
            <strong>Phone:</strong> +1 (800) 555-L3V3L
          </p>
        </section>

        <section>
          <h2>16. Agreement to Receive Text Messages</h2>
          <p>
            By providing your mobile number, you agree that <strong>L3V3LMATCHES by Nimble Data Solutions LLC</strong> may send you periodic SMS or MMS messages containing but not limited to important information, updates, deals, and specials.
          </p>
          <ul>
            <li>You will receive up to 4 messages per month.</li>
            <li>You may unsubscribe at any time by texting the word STOP to <strong>833-861-1131</strong>. You may receive a subsequent message confirming your unsubscribe request.</li>
            <li>For help, send the word HELP to <strong>833-861-1131</strong></li>
            <li>Message and data rates may apply.</li>
            <li>United States Participating Carriers Include AT&T, T-Mobile, Verizon Wireless, Sprint, Boost, U.S. Cellular, MetroPCS, InterOp, Cellcom, C Spire Wireless, Cricket, Virgin Mobile and others.</li>
            <li>T-Mobile is not liable for delayed or undelivered messages.</li>
            <li>You agree to notify us of any changes to your mobile number and update your account with us to reflect this change.</li>
            <li>By submitting your information in connection with this SMS service may include your cell phone number, your carrier's name, and the date, time and content of your messages, as well as other information that you provide. We may use this information to contact you and to provide the services you request from us.</li>
            <li>By downloading or otherwise using the service, you acknowledge and agree that you have the right to change and/or terminate the service at any time, with or without cause and/or advance notice.</li>
          </ul>
          <p>
            <strong>Questions?</strong> Contact <strong>L3V3LMATCHES by Nimble Data Solutions LLC</strong> at +1 (800) 555-L3V3L.
          </p>
          <p>
            <strong>Will I be charged for the text messages I receive?</strong><br />
            Though <strong>L3V3LMATCHES by Nimble Data Solutions LLC</strong> will never charge you for the text messages you receive, depending on your phone plan, you may see some charges from your mobile provider. Please reach out to your wireless provider if you have questions about your text or data plan.
          </p>
        </section>

        <section>
          <h2>17. Entire Agreement</h2>
          <p>
            These Terms, together with the Privacy Policy and Community Guidelines, constitute the entire agreement between you and us regarding the use of the Service.
          </p>
        </section>

        <div className="acceptance-notice">
          <p>
            <strong>BY CLICKING "I AGREE" OR BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE.</strong>
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default TermsOfService;
